// Define all available 3D model types for visualization
export enum ThreeDModels {
  EV_CHARGER = 'evcharger',
  GREENHOUSE = 'greenhouse',
  RED_HOUSE = 'red_house',
  BEDROOM = 'bedroom',
  TEMPERATURE = 'temperature',
  PRESSURE = 'pressure',
  MOTION = 'motion',
  LIGHT = 'light',
  VOLTAGE = 'voltage',
  POWER = 'power',
  CURRENT = 'current',
  ENERGY = 'energy',
}

// NOTE: All model files should be placed in the /public/models/ directory

// Define model configurations with URLs and metadata
interface Model {
  name: string;
  url: string;
  description: string;
}

export const modelsConfig = {
  evcharger: {
    name: 'EV Charging Station',
    url: '/models/ev_charger.glb', // Using relative path starting from public
    description: 'Electric Vehicle Charging Station with power monitoring',
  },
  greenhouse: {
    name: 'Smart Greenhouse',
    url: '/models/greenhouse.glb', // Using relative path starting from public
    description: 'Greenhouse with environmental monitoring sensors',
  },
  redHouse: {
    name: 'Red House',
    url: '/models/red_house.glb', // Using relative path starting from public
    description: 'Red house with house monitoring system',
  },
  bedroom: {
    name: 'Bedroom',
    url: '/models/bedroom.glb', // Using relative path starting from public
    description: 'Smart bedroom',
  },
  temperature: {
    name: 'Temperature Sensor',
    url: '/models/temperature.glb', // Using relative path starting from public
    description: 'Temperature measurement device',
  },
  pressure: {
    name: 'Pressure Sensor',
    url: '/models/pressure.glb',
    description: 'Atmospheric or system pressure sensor',
  },
  motion: {
    name: 'Motion Sensor',
    url: '/models/motion.glb',
    description: 'Motion detection sensor',
  },
  light: {
    name: 'Light Sensor',
    url: '/models/light.glb',
    description: 'Light level measurement device',
  },
  voltage: {
    name: 'Voltage Sensor',
    url: '/models/voltage.glb',
    description: 'Electrical voltage measurement device',
  },
  power: {
    name: 'Power Sensor',
    url: '/models/power-sensor.glb',
    description: 'Electrical power measurement device',
  },
  current: {
    name: 'Current Sensor',
    url: '/models/current-sensor.glb',
    description: 'Electrical current measurement device',
  },
  energy: {
    name: 'Energy Meter',
    url: '/models/energy-meter.glb',
    description: 'Energy consumption measurement device',
  },
};

// Sensor type mapping to appropriate 3D models
export const sensorTypeToModelMapping = {
  temperature: ThreeDModels.TEMPERATURE,
  pressure: ThreeDModels.PRESSURE,
  motion: ThreeDModels.MOTION,
  light: ThreeDModels.LIGHT,
  voltage: ThreeDModels.VOLTAGE,
  power: ThreeDModels.POWER,
  current: ThreeDModels.CURRENT,
  energy: ThreeDModels.ENERGY,
  // Add more mappings as needed
  default: ThreeDModels.TEMPERATURE, // Fallback
};

// Default display names for sensor types
export const sensorTypeDisplayNames: Record<string, string> = {
  temperature: 'Temperature',
  humidity: 'Humidity',
  pressure: 'Pressure',
  light: 'Light',
  motion: 'Motion',
  voltage: 'Voltage',
  power: 'Power Output',
  current: 'Current',
  energy: 'Energy Usage',
  proximity: 'Proximity',
  default: 'Sensor',
};

// Default colors for sensor types
export const sensorTypeColors: Record<string, string> = {
  temperature: '#ff4444',
  humidity: '#44aaff',
  pressure: '#8844ff',
  light: '#ffaa44',
  motion: '#44ffaa',
  voltage: '#ffaa22',
  power: '#44ff44',
  current: '#22aaff',
  energy: '#ff44aa',
  proximity: '#aaaaff',
  default: '#888888',
};

// Default units for sensor types
export const sensorTypeUnits: Record<string, string> = {
  temperature: '°C',
  humidity: '%',
  pressure: 'hPa',
  light: 'lux',
  motion: '',
  voltage: 'V',
  power: 'kW',
  current: 'A',
  energy: 'kWh',
  proximity: 'm',
  default: '',
};

export const modelsList: Model[] = Object.values(modelsConfig);
